import { useCallback, useEffect, useRef, useState } from 'react';

import { t } from '@ping/helpers';
import { Toast } from '@ping/uikit';
import { DateTime } from '@ping/utils';
import { NotificationEvents, useNotificationWS, WalletVerificationNotificationStatus } from '@ping/websockets';

/**
 * Raised by the caller when the QR code becomes visible and dismissed here once the transfer
 * reaches any terminal state, mirroring the KYC card's `pendingDataToastId`.
 */
export const WALLET_VERIFICATION_PENDING_TOAST_ID = 'walletVerificationPendingToastId';

/**
 * How long to wait for the next status before giving up on a transfer.
 *
 * A terminal failure reported on the delivery callback rather than as a status transition is
 * handled entirely server-side — the request row is cancelled and nothing is published — so
 * without this the QR code would stay locked behind the spinner with no way forward. The backend
 * has already freed the request by then, so retrying opens a fresh one.
 */
const STALLED_TRANSFER_TIMEOUT = DateTime.minutes(3);

/**
 * Statuses that mean CorePass is working on the transfer, so the QR code must not be scanned or
 * refreshed again.
 *
 * `Pending` is deliberately absent: the connector sends it when the QR code is generated, before
 * the user has scanned anything, so treating it as progress would lock the code immediately.
 */
const IN_PROGRESS_STATUSES: WalletVerificationNotificationStatus[] = [
  WalletVerificationNotificationStatus.Accepted,
  WalletVerificationNotificationStatus.Initiating,
  WalletVerificationNotificationStatus.InitiationSubmitted,
  WalletVerificationNotificationStatus.Initiated,
  WalletVerificationNotificationStatus.ValidityCheck,
  WalletVerificationNotificationStatus.ValiditySucceeded,
  WalletVerificationNotificationStatus.Confirming,
  WalletVerificationNotificationStatus.ConfirmSubmitted,
  WalletVerificationNotificationStatus.Confirmed,
  WalletVerificationNotificationStatus.CallbackSucceeded,
  WalletVerificationNotificationStatus.FinishSucceeded,
];

const FAILED_STATUSES: WalletVerificationNotificationStatus[] = [
  WalletVerificationNotificationStatus.InitiationFailed,
  WalletVerificationNotificationStatus.ValidityFailed,
  WalletVerificationNotificationStatus.ConfirmFailed,
  WalletVerificationNotificationStatus.CallbackFailed,
  WalletVerificationNotificationStatus.FinishFailed,
];

interface IWalletVerificationToast {
  type: 'progress' | 'error';
  /*
    Resolved when the toast is shown rather than when this module loads, so a language change
    between page load and the notification arriving is honoured.
  */
  title: () => string;
  description?: () => string;
}

const DO_NOT_CLOSE_COREPASS = () => t("Please don't close CorePass while the request is being processed");

const TOAST_MESSAGES_CONTENT = new Map<WalletVerificationNotificationStatus, IWalletVerificationToast>()
  .set(WalletVerificationNotificationStatus.Accepted, {
    type: 'progress',
    title: () => t('Address request accepted...'),
    description: DO_NOT_CLOSE_COREPASS,
  })
  .set(WalletVerificationNotificationStatus.Initiating, {
    type: 'progress',
    title: () => t('Starting address transfer'),
    description: DO_NOT_CLOSE_COREPASS,
  })
  .set(WalletVerificationNotificationStatus.InitiationSubmitted, {
    type: 'progress',
    title: () => t('Starting address transfer'),
    description: DO_NOT_CLOSE_COREPASS,
  })
  .set(WalletVerificationNotificationStatus.InitiationFailed, {
    type: 'error',
    title: () => t('Address transfer failed'),
  })
  .set(WalletVerificationNotificationStatus.ValidityCheck, {
    type: 'progress',
    title: () => t('Checking integrity of submitted address'),
    description: DO_NOT_CLOSE_COREPASS,
  })
  .set(WalletVerificationNotificationStatus.ValidityFailed, {
    type: 'error',
    title: () => t('Checking integrity of submitted address failed'),
  })
  .set(WalletVerificationNotificationStatus.Confirming, {
    type: 'progress',
    title: () => t('Confirming address transfer...'),
    description: DO_NOT_CLOSE_COREPASS,
  })
  .set(WalletVerificationNotificationStatus.ConfirmSubmitted, {
    type: 'progress',
    title: () => t('Confirming address transfer...'),
    description: DO_NOT_CLOSE_COREPASS,
  })
  .set(WalletVerificationNotificationStatus.Confirmed, {
    type: 'progress',
    title: () => t('Confirming address transfer...'),
    description: DO_NOT_CLOSE_COREPASS,
  })
  .set(WalletVerificationNotificationStatus.ConfirmFailed, {
    type: 'error',
    title: () => t('Confirming address transfer failed'),
  })
  .set(WalletVerificationNotificationStatus.CallbackFailed, {
    type: 'error',
    title: () => t('Address transfer failed'),
  })
  .set(WalletVerificationNotificationStatus.FinishFailed, {
    type: 'error',
    title: () => t('Address transfer failed'),
  });

interface IUseWalletVerificationProgressOptions {
  /** Called when the address has been delivered and stored, i.e. the verification succeeded. */
  onVerified?: (network?: string) => void;
}

/**
 * Tracks an external wallet address verification over the notification socket.
 *
 * The backend publishes one `OnWalletVerificationStatusChanged` per connector status transition and
 * a single `OnWalletVerified` on delivery. Both are subscribed here through the same connection,
 * routed by per-event callbacks so a status update cannot be mistaken for a completed verification.
 */
export const useWalletVerificationProgress = (options: IUseWalletVerificationProgressOptions = {}) => {
  const { subscribe } = useNotificationWS();
  const [status, setStatus] = useState<WalletVerificationNotificationStatus>(null);
  /*
    Sticky rather than derived from `status`: once CorePass has the request, an unrecognised or
    out-of-order status must not quietly unlock the QR code. Only a failure, a success or an
    explicit reset clears it.
  */
  const [isProcessing, setIsProcessing] = useState(false);
  const lastToastId = useRef<string>(null);
  const stalledTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const onVerified = useRef(options.onVerified);

  onVerified.current = options.onVerified;

  const dismissToasts = useCallback(() => {
    Toast.dismiss(WALLET_VERIFICATION_PENDING_TOAST_ID);

    if (lastToastId.current) {
      Toast.dismiss(lastToastId.current);
      lastToastId.current = null;
    }
  }, []);

  const clearStalledTimer = useCallback(() => {
    if (stalledTimer.current) {
      clearTimeout(stalledTimer.current);
      stalledTimer.current = null;
    }
  }, []);

  /* Restarted on every status, so the window is "silence since the last update", not total duration. */
  const restartStalledTimer = useCallback(() => {
    clearStalledTimer();

    stalledTimer.current = setTimeout(() => {
      stalledTimer.current = null;
      dismissToasts();
      setStatus(null);
      setIsProcessing(false);
      Toast.error({
        title: t('Wallet address verification timed out'),
        text: t('CorePass stopped responding. Please scan the QR code again to retry.'),
      });
    }, STALLED_TRANSFER_TIMEOUT);
  }, [clearStalledTimer, dismissToasts]);

  const reset = useCallback(() => {
    clearStalledTimer();
    dismissToasts();
    setStatus(null);
    setIsProcessing(false);
  }, [clearStalledTimer, dismissToasts]);

  useEffect(() => {
    subscribe<{ status: WalletVerificationNotificationStatus }>(
      NotificationEvents.OnWalletVerificationStatusChanged,
      message => {
        const nextStatus = message?.status;

        if (!nextStatus) return;

        setStatus(nextStatus);

        if (IN_PROGRESS_STATUSES.includes(nextStatus)) {
          setIsProcessing(true);
          restartStalledTimer();
        }

        if (FAILED_STATUSES.includes(nextStatus)) {
          setIsProcessing(false);
          clearStalledTimer();
        }

        const toast = TOAST_MESSAGES_CONTENT.get(nextStatus);

        if (!toast) return;

        dismissToasts();
        lastToastId.current = nextStatus;

        Toast[toast.type]({
          title: toast.title(),
          text: toast.description?.(),
          options: { autoClose: false, toastId: nextStatus },
        });
      }
    );

    subscribe<{ network?: string }>(NotificationEvents.OnWalletVerified, message => {
      clearStalledTimer();
      dismissToasts();
      setStatus(null);
      setIsProcessing(false);
      onVerified.current?.(message?.network);
    });
  }, [subscribe, dismissToasts, restartStalledTimer, clearStalledTimer]);

  useEffect(() => {
    return () => {
      clearStalledTimer();
      dismissToasts();
    };
  }, [clearStalledTimer, dismissToasts]);

  return {
    status,
    isProcessing,
    isFailed: FAILED_STATUSES.includes(status),
    reset,
  };
};
